<script setup lang="ts">
  import { computed, ref, useId, watch } from 'vue';
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import FontIcon from '../FontIcon/FontIcon.vue';
  import type { AccordionProps } from './Accordion.types';
  import {
    accordionClass,
    attributeClass,
    contentClass,
    descriptionClass,
    descriptionRowClass,
    dividerClass,
    headerClass,
    leadingClass,
    regionClass,
    regionCollapsedClass,
    regionInnerClass,
    regionTransitionClass,
    tagsClass,
    titleButtonClass,
    titleClass,
    toggleClass,
    toggleIconClass,
  } from './Accordion.css';

  const props = withDefaults(defineProps<AccordionProps>(), {
    open: undefined,
    defaultOpen: false,
    unmountOnClose: false,
  });

  const emit = defineEmits<{
    'update:open': [open: boolean];
  }>();

  const uid = useId();
  const resolvedTestId = computed(() => props.testId ?? `ds-accordion-${uid}`);
  const contentId = `ds-accordion-content-${uid}`;
  const triggerId = `ds-accordion-trigger-${uid}`;

  const internalOpen = ref(props.defaultOpen);
  const isOpen = computed(() => props.open ?? internalOpen.value);

  const contentMounted = ref(isOpen.value);
  const shouldRenderContent = computed(
    () => !props.unmountOnClose || contentMounted.value,
  );

  watch(isOpen, (open) => {
    contentMounted.value = open;
  });

  const toggle = () => {
    const next = !isOpen.value;
    if (props.open === undefined) internalOpen.value = next;
    emit('update:open', next);
  };

  const chevronIcon = computed(() =>
    isOpen.value ? TUNDR_ICONS.CHEVRON_UP : TUNDR_ICONS.CHEVRON_DOWN,
  );

  const regionTransition = {
    enterActiveClass: regionTransitionClass,
    leaveActiveClass: regionTransitionClass,
    enterFromClass: regionCollapsedClass,
    leaveToClass: regionCollapsedClass,
  };
</script>

<template>
  <div :class="accordionClass" :data-testid="resolvedTestId">
    <div :class="headerClass">
      <div v-if="$slots.leading" :class="leadingClass">
        <slot name="leading" />
      </div>
      <div :class="contentClass">
        <div :class="attributeClass">
          <h3 :class="titleClass">
            <button
              :id="triggerId"
              type="button"
              :class="titleButtonClass"
              :aria-expanded="isOpen"
              :aria-controls="contentId"
              :data-testid="`${resolvedTestId}-toggle`"
              @click="toggle"
            >
              {{ title }}
              <span :class="toggleClass" aria-hidden="true">
                <FontIcon :icon-name="chevronIcon" :class="toggleIconClass" />
              </span>
            </button>
          </h3>
          <div v-if="description || $slots.action" :class="descriptionRowClass">
            <span v-if="description" :class="descriptionClass">
              {{ description }}
            </span>
            <slot name="action" />
          </div>
        </div>
        <div v-if="$slots.tags" :class="tagsClass">
          <slot name="tags" />
        </div>
      </div>
    </div>
    <Transition v-bind="regionTransition">
      <div
        v-show="isOpen"
        :id="contentId"
        role="region"
        :aria-labelledby="triggerId"
        :class="regionClass"
        :data-testid="`${resolvedTestId}-content`"
      >
        <div :class="regionInnerClass">
          <div :class="dividerClass" />
          <slot v-if="shouldRenderContent" />
        </div>
      </div>
    </Transition>
  </div>
</template>
